import { get } from "lodash";
import { useEffect, useState } from "react";
import { trackPromise } from "react-promise-tracker";
import { Button, constructUriWithSearchParams } from "@shared/ui";
import { InlineError } from "../components/Field";
import Loader from "../components/Loader";
import StepBody from "../components/StepBody";
import StepFooter from "../components/StepFooter";
import StepHeading from "../components/StepHeading";
import { updateIgnition } from "../lib/axle";
import {
  generateLinkErrorMessage,
  generateManualErrorMessage,
  isIntegrationV123,
} from "../lib/utility";

const Failed = ({
  step,
  session,
  client,
  previousStep,
  linkError,
  manualError,
  posthog,
  nextStep,
  loginInformation,
  integration,
  setShowNav,
}) => {
  const [error, setError] = useState(null);

  const [isRedirectEnabled, setRedirectEnabled] = useState(
    session.redirectUri || session.origin
  );

  // When confirmationBeforeRedirect is true, show the failed message and wait for user to click before redirecting
  const confirmationBeforeRedirect = get(
    session,
    "config.confirmationBeforeRedirect",
    false
  );

  // Track whether the async fail flow has completed (so the confirmation button can be shown)
  const [isProcessingComplete, setIsProcessingComplete] = useState(false);

  let message = "";

  switch (previousStep) {
    case "carrier-error":
      message = generateLinkErrorMessage(linkError);
      break;
    case "manual-document-error":
      message = generateManualErrorMessage(manualError);
      break;
    case "mfa-options-error":
      message = linkError;
      break;
    default:
      message = "The Axle service cannot be reached. Please try again later.";
      break;
  }

  useEffect(() => {
    if (isIntegrationV123(loginInformation, integration, session)) {
      setShowNav(false);
    }

    posthog.capture("$pageview", { step });
    const fail = async () => {
      // Construct webhook event
      const lastEvent = {
        type: `ignition.errored`,
        data: {
          token: session.id,
          message,
          user: session.user,
          client: client.id,
        },
        createdAt: new Date().toISOString(),
      };

      // Update Ignition session
      const params = {
        status: "errored",
        lastEvent,
      };
      const [updateIgnitionResponse, updateIgnitionError] =
        await updateIgnition(session.id, params);

      if (updateIgnitionError) {
        switch (get(updateIgnitionError, "code")) {
          case 500:
            console.log(JSON.stringify(updateIgnitionError));
            nextStep("axle-error");
            return;
          default:
            nextStep("connection-error");
            return;
        }
      }

      if (isRedirectEnabled) {
        if (confirmationBeforeRedirect) {
          // Wait for user confirmation before redirecting
          setIsProcessingComplete(true);
        } else {
          console.log(`Redirect is enabled, sending event and redirecting...`);
          await sendEventAndRedirect();
        }
      }
    };
    trackPromise(fail());
  }, []);

  const sendEventAndRedirect = async () => {
    // Params for redirectUri
    const paramsToAdd = {
      status: "error",
      message: encodeURIComponent(message),
      client: client.id,
    };

    // Send message to parent window if initialized in an iframe
    if (session.origin) {
      const sessionOrigin = session.origin;
      window.parent.postMessage(paramsToAdd, sessionOrigin);
      console.log(`Sending message to session origin ${session.origin}`);
      console.log(
        "Message sent to parent window",
        JSON.stringify(paramsToAdd, null, 2)
      );
    }

    if (session.redirectUri) {
      const constructedUri = constructUriWithSearchParams(
        session.redirectUri,
        paramsToAdd
      );
      // await new Promise((resolve) => setTimeout(resolve, 1500));
      window.location.href = constructedUri;
    }
  };

  // Handler for the confirmation button click
  const handleConfirmRedirect = async () => {
    await sendEventAndRedirect();
  };

  // Determine whether to show the message content vs a loader
  const showMessageContent =
    !isRedirectEnabled || (confirmationBeforeRedirect && isProcessingComplete);

  return (
    <>
      <StepBody
        className={showMessageContent ? "items-center gap-4 text-center" : undefined}
      >
        {error && (
          <div className="rounded border border-danger-border p-3">
            <InlineError>{error}</InlineError>
          </div>
        )}

        {showMessageContent ? (
          <>
            <div
              style={{ backgroundImage: `url("./icons/manual-required.svg")` }}
              className="mt-8 h-32 w-full flex-none bg-contain bg-center bg-no-repeat"
              aria-hidden
            ></div>
            <StepHeading
              align="center"
              title={
                get(session, "config.failed.header") ??
                "Sorry, there is an error."
              }
              description={
                get(session, "config.failed.subheader") ?? (
                  <>
                    The <b className="font-semibold text-ink">{client.displayName}</b>{" "}
                    team will take it from here.
                  </>
                )
              }
            />
          </>
        ) : (
          <Loader isOpen={true} />
        )}
      </StepBody>
      {showMessageContent && isRedirectEnabled && confirmationBeforeRedirect && (
        <StepFooter>
          <Button
            onClick={handleConfirmRedirect}
            name="failed-confirm-redirect"
          >
            Continue
          </Button>
        </StepFooter>
      )}
    </>
  );
};

export default Failed;
